import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Loader2, Lock, Pencil, Save, Trash2, X } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, FieldRow, PageHead, Section, TONES, inputCls } from "@/components/account/Shell";
import { Cell, Dash, Filters, IconBtn, PillRow, Sheet, Tag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import Billboard from "@/components/mall/Billboard";
import { themeOf } from "@/lib/mall";

const TONE = TONES.marketing;
const BLANK = { id: null, headline: "", subhead: "", image: null, cta_label: "", cta_url: "", product_kind: "", product_slug: "", sort_order: 0 };
const STATUS = { pending: "In review", active: "Live on your shop page", rejected: "Sent back" };
const SHOW = [
    { key: "all", label: "Everything", match: () => true },
    { key: "active", label: "Live", match: (b) => b.status === "active" },
    { key: "pending", label: "In review", match: (b) => b.status === "pending" },
    { key: "rejected", label: "Sent back", match: (b) => b.status === "rejected" },
];
const COLS = [
    { key: "image", label: "Photo", w: "76px" },
    { key: "headline", label: "Headline", sub: "the big line · the small line", w: "minmax(240px,2fr)" },
    { key: "cta", label: "Button", sub: "label → link", w: "minmax(180px,1.2fr)" },
    { key: "product", label: "Shows", sub: "the product card on it", w: "minmax(140px,1fr)" },
    { key: "order", label: "Order", sub: "lower hangs first", w: "70px" },
    { key: "status", label: "Status", sub: "the office's note if it came back", w: "minmax(150px,1fr)" },
    { key: "actions", label: "Actions", sub: "edit · remove", w: "90px" },
];

export default function StoreBillboards() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [options, setOptions] = useState([]);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [show, setShow] = useState("all");
    const fileRef = useRef(null);

    const load = () => api.get("/store/billboards").then(({ data }) => {
        setData(data);
        if (!data.store) return;
        api.get(`/stores/${data.store.slug}`).then(({ data: page }) => setOptions([
            { kind: "gang", slug: "", label: "DTF gang sheets (designer)" },
            ...(page.shirts || []).map((p) => ({ kind: "shirt", slug: p.slug, label: `Apparel · ${p.name}` })),
            ...(page.signs || []).map((p) => ({ kind: "sign", slug: p.slug, label: `Signs · ${p.name}` })),
        ])).catch(() => { });
    }).catch(() => { });

    useEffect(() => { load(); }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const pick = async (file) => {
        if (!file) return;
        setUploading(true);
        try { const rec = await uploadArtwork(file); setForm((f) => ({ ...f, image: rec.url })); }
        catch { toast.error("Upload failed"); }
        finally { setUploading(false); }
    };

    const save = async (e) => {
        e?.preventDefault();
        if (!form.headline.trim()) { toast.error("Your billboard needs a headline"); return; }
        setBusy(true);
        try {
            const { data: res } = await api.post("/store/billboards", {
                ...form, product_kind: form.product_kind || null, product_slug: form.product_slug || null,
                sort_order: Number(form.sort_order) || 0,
            });
            toast.success(res.note);
            setForm(BLANK);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        try { await api.delete(`/store/billboards/${id}`); toast.success("Sign taken down"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (data && !data.cap) {
        return (
            <BackOffice>
                <div className="mx-auto max-w-2xl py-20 text-center" data-testid="billboards-locked">
                    <Lock size={32} className="mx-auto text-zinc-400" />
                    <h1 className="mt-3 font-display text-2xl font-black tracking-tight text-zinc-900">Billboards hang in front of a store.</h1>
                    <p className="mt-2 text-sm text-zinc-800">
                        A Pro Shop or a Virtual Brick & Mortar hangs 1 sign at the top of its shop page.
                        Kiosks post classified ads instead.
                    </p>
                    <Link to="/account/sell" className="mt-4 inline-block"><Btn tone="solid" testid="billboards-upgrade">See the spaces</Btn></Link>
                </div>
            </BackOffice>
        );
    }

    const store = data?.store;
    const boards = data?.billboards || [];
    const used = boards.filter((b) => b.status !== "rejected").length;
    const shown = boards.filter(SHOW.find((s) => s.key === show).match);
    const preview = { ...form, store: { ...(store || {}), theme: themeOf(store) }, product: null };
    const photoBtn = (
        <>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="billboard-image-btn"
                className="flex h-[38px] w-24 items-center justify-center overflow-hidden border-2 border-dashed border-[#d0d0d0] bg-white hover:border-[#1a1a1a]">
                {uploading ? <Loader2 size={15} className="animate-spin text-[#4a4a4a]" />
                    : form.image ? <img src={mediaUrl(form.image)} alt="" className="h-full w-full object-cover" />
                        : <ImagePlus size={15} className="text-[#4a4a4a]" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </>
    );

    return (
        <BackOffice>
            <PageHead kicker="Marketing" tone={TONE} title="Your billboards" testid="billboards-title"
                sub="A billboard is the full-width sign at the top of your shop page, painted in your colors. Save it and it's up."
                right={data && (
                    <div className="text-right">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Slots used</p>
                        <p className="font-mono text-2xl font-black tabular-nums text-zinc-900" data-testid="billboards-count">
                            {used}<span className="text-zinc-600">/{data.cap}</span>
                        </p>
                    </div>
                )} />

            <Filters testid="billboards-filters">
                <PillRow label="Show" testid="billboards-show" value={show} onPick={setShow}
                    choices={SHOW.map((s) => ({ key: s.key, label: s.label, count: boards.filter(s.match).length }))} />
            </Filters>

            <div className="mt-3 space-y-3">
                <Section tone={TONE} pad={false} testid="my-billboards" title={`${shown.length} hanging now`}
                    note="edit one and it goes back for a look">
                    <Sheet cols={COLS} rows={shown} rowKey={(b) => b.id} testid="billboards-sheet" minWidth={1000}
                        rowTestid={(b) => `billboard-row-${b.id}`} active={(b) => form.id === b.id}
                        empty={boards.length ? "Nothing matches that filter." : "No signs hanging yet. Build your first one below."}
                        cells={(b) => ({
                            image: (
                                <Cell>
                                    <span className="h-8 w-14 shrink-0 overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                                        {b.image && <img src={mediaUrl(b.image)} alt="" className="h-full w-full object-cover" />}
                                    </span>
                                </Cell>
                            ),
                            headline: <Cell><Two a={b.headline} b={b.subhead} /></Cell>,
                            cta: <Cell>{b.cta_label || b.cta_url ? <Two a={b.cta_label || "Open"} b={b.cta_url} /> : <Dash why="No button on this sign" />}</Cell>,
                            product: <Cell>{b.product?.name || b.product_slug ? <Txt>{b.product?.name || b.product_slug}</Txt> : <Dash why="No product card" />}</Cell>,
                            order: <Cell><Txt mono right>{b.sort_order ?? 0}</Txt></Cell>,
                            status: (
                                <Cell className="flex-wrap">
                                    <Tag status={b.status} testid={`billboard-status-${b.id}`}>{STATUS[b.status] || b.status}</Tag>
                                    {b.review_note && <Txt sub cls="text-[#b42318]" title={b.review_note}>{b.review_note}</Txt>}
                                </Cell>
                            ),
                            actions: (
                                <Cell className="justify-end">
                                    <IconBtn onClick={() => setForm({ ...BLANK, ...b })} testid={`billboard-edit-${b.id}`} title="Edit this sign"><Pencil size={12} /></IconBtn>
                                    <IconBtn onClick={() => remove(b.id)} testid={`billboard-delete-${b.id}`} title="Take it down" danger><Trash2 size={12} /></IconBtn>
                                </Cell>
                            ),
                        })} />
                    {data && !boards.length && <p className="sr-only" data-testid="billboards-empty">No signs hanging yet.</p>}
                </Section>

                <Section tone={TONE} pad={false} testid="billboard-form" title={form.id ? "Edit this sign" : "Hang a new sign"}
                    note="one field per line · the preview under it updates as you type"
                    right={form.id ? (
                        <Btn onClick={() => setForm(BLANK)} testid="billboard-cancel"><X size={10} /> new sign instead</Btn>
                    ) : null}>
                    <form onSubmit={save}>
                        <FieldRow label="Headline" hint="the big line — keep it short and loud">
                            <input className={inputCls} placeholder="DETROIT'S GANG SHEET SHOP." value={form.headline} onChange={set("headline")} data-testid="billboard-headline" />
                        </FieldRow>
                        <FieldRow label="One line of copy" hint="what you want every shopper to know" top>
                            <textarea className={inputCls} rows={2} placeholder="What you want every shopper to know." value={form.subhead || ""} onChange={set("subhead")} data-testid="billboard-subhead" />
                        </FieldRow>
                        <FieldRow label="Background photo" hint="sits behind the headline, tinted in your color">
                            <div className="flex items-center gap-2">{photoBtn}{form.image && <Txt sub>photo set</Txt>}</div>
                        </FieldRow>
                        <FieldRow label="Button text" hint="what the button says">
                            <input className={`${inputCls} max-w-md`} placeholder="Build a gang sheet" value={form.cta_label || ""} onChange={set("cta_label")} data-testid="billboard-cta-label" />
                        </FieldRow>
                        <FieldRow label="Button link" hint="where it goes — a page in your shop">
                            <input className={`${inputCls} max-w-md`} placeholder="/shirts/dtf/gang-sheet" value={form.cta_url || ""} onChange={set("cta_url")} data-testid="billboard-cta-url" />
                        </FieldRow>
                        <FieldRow label="Show a product on it" hint="optional · a small product card on the sign">
                            <select className={`${inputCls} max-w-md`} data-testid="billboard-product"
                                value={`${form.product_kind || ""}|${form.product_slug || ""}`}
                                onChange={(e) => {
                                    const [kind, slug] = e.target.value.split("|");
                                    setForm((f) => ({ ...f, product_kind: kind, product_slug: slug || "" }));
                                }}>
                                <option value="|">No product card</option>
                                {options.map((o) => <option key={`${o.kind}|${o.slug}`} value={`${o.kind}|${o.slug}`}>{o.label}</option>)}
                            </select>
                        </FieldRow>
                        <FieldRow label="Order" hint="lower numbers hang first">
                            <input className={`${inputCls} max-w-[120px]`} type="number" min="0" value={form.sort_order} onChange={set("sort_order")} data-testid="billboard-sort" />
                        </FieldRow>
                        <div className="border-t border-[#e5e5e5] px-2.5 py-2">
                            <Btn type="submit" tone="solid" disabled={busy} testid="billboard-save">
                                {busy ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />} {form.id ? "Save changes" : "Hang it now"}
                            </Btn>
                        </div>
                    </form>
                </Section>

                <Section tone={TONE} pad={false} title="Preview" note="this is your sign at the top of your shop page">
                    <div className="overflow-hidden" data-testid="billboard-preview">
                        <Billboard board={preview} preview />
                    </div>
                </Section>
            </div>
        </BackOffice>
    );
}
